import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import { Box, CircularProgress, Typography } from '@mui/material';

let mermaidInitialized = false;

const initMermaid = () => {
  if (mermaidInitialized) return;
  mermaid.initialize({
    startOnLoad: false,
    theme: 'dark',
    securityLevel: 'strict',
    fontFamily: 'inherit',
    themeVariables: {
      primaryColor: '#7c3aed',
      primaryTextColor: '#fafafa',
      primaryBorderColor: '#a78bfa',
      lineColor: '#71717a',
      secondaryColor: '#1e1b4b',
      tertiaryColor: '#0f0f12',
    },
  });
  mermaidInitialized = true;
};

const MermaidDiagram = ({ code, caption }) => {
  const containerRef = useRef(null);
  const [error, setError] = useState('');
  const [rendering, setRendering] = useState(true);

  useEffect(() => {
    if (!code?.trim()) return undefined;

    let cancelled = false;

    const render = async () => {
      setRendering(true);
      setError('');
      initMermaid();

      try {
        const id = `mermaid-${Math.random().toString(36).slice(2, 9)}`;
        const { svg } = await mermaid.render(id, code.trim());
        if (!cancelled && containerRef.current) {
          containerRef.current.innerHTML = svg;
        }
      } catch (err) {
        if (!cancelled) {
          setError('Could not render diagram');
          if (containerRef.current) containerRef.current.innerHTML = '';
        }
      } finally {
        if (!cancelled) setRendering(false);
      }
    };

    render();
    return () => { cancelled = true; };
  }, [code]);

  return (
    <Box
      sx={{
        my: 2.5,
        p: 2,
        borderRadius: '14px',
        border: '1px solid rgba(255,255,255,0.08)',
        bgcolor: 'rgba(255,255,255,0.02)',
        overflow: 'auto',
      }}
    >
      {caption && (
        <Typography sx={{ color: '#a1a1aa', fontSize: 12, fontWeight: 600, mb: 1.5 }}>
          {caption}
        </Typography>
      )}
      {rendering && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} sx={{ color: '#a78bfa' }} />
        </Box>
      )}
      {error && (
        <Typography sx={{ color: '#f87171', fontSize: 13 }}>{error}</Typography>
      )}
      <Box
        ref={containerRef}
        sx={{
          display: rendering ? 'none' : 'flex',
          justifyContent: 'center',
          '& svg': { maxWidth: '100%', height: 'auto' },
        }}
      />
    </Box>
  );
};

export default MermaidDiagram;